"use client"
import { useEffect, useState } from "react"

const SCROLL_THRESHOLD = 100
const FALLBACK_TIMEOUT_MS = 12_000

/**
 * Returns true once the user has scrolled past `scrollThreshold` pixels
 * or interacted with the page (touch/click), whichever comes first.
 * Falls back to true after `timeoutMs` for users who never scroll
 * (e.g. desktop with viewport already showing all content).
 */
export function useHasUserEngaged(
    scrollThreshold = SCROLL_THRESHOLD,
    timeoutMs = FALLBACK_TIMEOUT_MS
): boolean {
    const [engaged, setEngaged] = useState(false)

    useEffect(() => {
        if (engaged) return

        const engage = () => setEngaged(true)

        const handleScroll = () => {
            if (window.scrollY > scrollThreshold) {
                engage()
            }
        }

        window.addEventListener("scroll", handleScroll, { passive: true })
        window.addEventListener("click", engage, { once: true })
        window.addEventListener("touchstart", engage, { once: true })

        const timer = setTimeout(engage, timeoutMs)

        handleScroll()

        return () => {
            window.removeEventListener("scroll", handleScroll)
            window.removeEventListener("click", engage)
            window.removeEventListener("touchstart", engage)
            clearTimeout(timer)
        }
    }, [engaged, scrollThreshold, timeoutMs])

    return engaged
}
